"use client";

import Link from "next/link";
import { useEffect, useId, useRef, useState } from "react";

import { ChevronDown } from "lucide-react";

import { cn } from "@louez/utils";

import { PortMegaMenuPanel } from "@/components/port-portal/port-mega-menu-panel";
import {
  buildPortMegaMenus,
  type PortMegaMenuConfig,
} from "@/components/port-portal/util.port-mega-menu";

type PortMegaMenuProps = {
  boatsUrl: string;
  portalUrl?: string;
  servicesHrefBySlug: Record<string, string>;
};

export const PortMegaMenu = ({
  boatsUrl,
  portalUrl = "",
  servicesHrefBySlug,
}: PortMegaMenuProps) => {
  const menus = buildPortMegaMenus(boatsUrl, servicesHrefBySlug, portalUrl);
  const portalHref = portalUrl.replace(/\/$/, "");
  const experiencesHref = `${portalHref}/experiencias`;
  const [flashSales, setFlashSales] = useState<Array<{
    slug: string;
    title: string;
    summary: string;
    imageUrl: string | null;
  }>>([]);
  const [openId, setOpenId] = useState<PortMegaMenuConfig["id"] | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootId = useId();

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openMenu = (id: PortMegaMenuConfig["id"]) => {
    clearCloseTimer();
    setOpenId(id);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpenId(null), 120);
  };

  useEffect(() => {
    fetch("/api/flash-sales")
      .then((response) => (response.ok ? response.json() : []))
      .then(setFlashSales)
      .catch(() => setFlashSales([]));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenId(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearCloseTimer();
    };
  }, []);

  const resolvedMenus = menus.map((menu) =>
    menu.id !== "experiencias"
      ? menu
      : {
          ...menu,
          columns: [
            {
              heading: "Ofertas activas",
              links: flashSales.slice(0, 4).map((sale) => ({
                title: sale.title,
                description: sale.summary,
                href: `${experiencesHref}/${sale.slug}`,
              })),
            },
            {
              heading: "Descubrir",
              links: [
                { title: "Todas las experiencias", description: "Consulta las flash-sales activas del puerto.", href: experiencesHref },
                { title: "Cómo funciona", description: "Reserva, descarga tu ticket QR y paga al consumir la experiencia.", href: experiencesHref },
              ],
            },
          ],
          featured: {
            heading: "Ahora en el puerto",
            items: flashSales.slice(0, 3).map((sale) => ({
              title: sale.title,
              href: `${experiencesHref}/${sale.slug}`,
              imageUrl: sale.imageUrl ?? "/images/ecosystem/port.jpg",
            })),
          },
          promo: { ...menu.promo, title: "Flash-sales del puerto", description: "Ofertas consecutivas y por tiempo limitado.", cta: "Ver experiencias", href: experiencesHref },
        },
  );
  const openMenuConfig = resolvedMenus.find((menu) => menu.id === openId) ?? null;

  return (
    <div className="relative hidden lg:block" onMouseLeave={scheduleClose}>
      <nav
        className="flex items-center gap-1 text-sm font-medium"
        aria-label="Secciones del puerto"
      >
        <a
          href={`${portalHref}#explorar`}
          className="rounded-full px-3 py-2 transition-colors hover:bg-white/10"
          onMouseEnter={scheduleClose}
        >
          Explorar
        </a>

        {resolvedMenus.map((menu) => {
          const isOpen = openId === menu.id;
          const panelId = `${rootId}-${menu.id}`;

          return (
            <div key={menu.id} className="relative" onMouseEnter={() => openMenu(menu.id)}>
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-2 transition-colors",
                  isOpen ? "bg-white/15 text-white" : "hover:bg-white/10",
                )}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenId(isOpen ? null : menu.id)}
              >
                {menu.label}
                <ChevronDown
                  className={cn("size-3.5 transition-transform", isOpen && "rotate-180")}
                />
              </button>
            </div>
          );
        })}

        <Link
          href={`${portalHref}/actualidad`}
          className="rounded-full px-3 py-2 transition-colors hover:bg-white/10"
          onMouseEnter={scheduleClose}
        >
          Actualidad
        </Link>
      </nav>

      {openMenuConfig ? (
        <div
          id={`${rootId}-${openMenuConfig.id}`}
          className="fixed top-20 left-1/2 z-40 w-[min(72rem,calc(100vw-2.5rem))] -translate-x-1/2 pt-3"
          onMouseEnter={clearCloseTimer}
        >
          <PortMegaMenuPanel menu={openMenuConfig} />
        </div>
      ) : null}
    </div>
  );
};
